// Utility functions for consolidating multiple RM/Support results

/**
 * Consolidate multiple RM and Support results into a single combined result
 * @param {Array} results - Array of result objects from individual RMs/Support
 * @returns {Object} Consolidated result with combined metrics
 */
export const consolidateResults = (results) => {
  if (!results || results.length === 0) {
    return null;
  }
  
  // If only one result, return it as-is
  if (results.length === 1) {
    return results[0];
  }
  
  // Step 1: Merge monthly schedules
  const consolidatedMonthly = mergeMonthlySchedules(results);
  
  // Step 2: Calculate annual summary from monthly data
  const consolidatedAnnual = calculateAnnualFromMonthly(consolidatedMonthly);
  
  // Step 3: Calculate total metrics
  const consolidatedMetrics = calculateConsolidatedMetrics(consolidatedAnnual, consolidatedMonthly);
  
  // Step 4: Calculate cumulative payback
  const cumulativePayback = calculateCumulativePayback(consolidatedMonthly);
  
  // Use yield curves from first result (assuming all use same curves)
  const yieldCurves = results[0].yieldCurves;
  
  return {
    monthlySchedule: consolidatedMonthly,
    annualSummary: consolidatedAnnual,
    totalMetrics: consolidatedMetrics,
    cumulativePayback,
    yieldCurves,
    isConsolidated: true
  };
};

/**
 * Merge monthly schedules from multiple results
 * Handles different start dates and timeline alignment
 */
const mergeMonthlySchedules = (results) => {
  // Create a map of all unique dates across all results
  const dateMap = new Map();
  
  // Collect all dates and initialize with zeros
  results.forEach(result => {
    if (result?.monthlySchedule) {
      result.monthlySchedule.forEach(month => {
        if (!dateMap.has(month.index)) {
          // Initialize with all numeric fields set to 0
          dateMap.set(month.index, {
            index: month.index,
            interest_income_loans: 0,
            interest_income_lines: 0,
            origination_fees: 0,
            unused_commitment_fees: 0,
            interest_expense: 0,
            salary_expense: 0,
            benefits_expense: 0,
            incentive_compensation_expense: 0,
            discretionary_expense: 0,
            deferred_cost_amortization: 0,
            deferred_origination_fee_amortization: 0,
            provision_expense: 0,
            non_interest_income: 0,
            non_interest_expense: 0,
            net_income: 0,
            loan_balance: 0,
            line_balance: 0,
            deposit_balance: 0,
            line_commitment_balance: 0,
            deferred_origination_fees: 0,
            deferred_costs: 0,
            total_assets: 0,
            total_liabilities: 0,
            total_equity: 0,
            cumulative_profit: 0
          });
        }
      });
    }
  });
  
  // Sum values for each date across all results
  results.forEach(result => {
    if (result?.monthlySchedule) {
      result.monthlySchedule.forEach(month => {
        const consolidated = dateMap.get(month.index);
        
        // Sum all numeric fields
        Object.keys(month).forEach(key => {
          if (key !== 'index' && key !== 'period' && typeof month[key] === 'number') {
            consolidated[key] = (consolidated[key] || 0) + (month[key] || 0);
          }
        });
      });
    }
  });
  
  // Convert map to sorted array and recalculate cumulative profit
  const sortedMonthly = Array.from(dateMap.values()).sort((a, b) => 
    new Date(a.index) - new Date(b.index)
  );
  
  // Recalculate cumulative profit
  let cumulative = 0;
  sortedMonthly.forEach(month => {
    cumulative += month.net_income;
    month.cumulative_profit = cumulative;
  });
  
  return sortedMonthly;
};

/**
 * Calculate annual summary from consolidated monthly data
 */
const calculateAnnualFromMonthly = (monthlySchedule) => {
  if (!monthlySchedule || monthlySchedule.length === 0) {
    return [];
  }
  
  // Group by year
  const yearMap = new Map();
  
  monthlySchedule.forEach(month => {
    const date = new Date(month.index);
    const year = date.getFullYear();
    
    if (!yearMap.has(year)) {
      yearMap.set(year, {
        index: `${year}-12-31`,
        months: [],
        // Initialize sums
        interest_income_loans: 0,
        interest_income_lines: 0,
        origination_fees: 0,
        unused_commitment_fees: 0,
        interest_expense: 0,
        salary_expense: 0,
        benefits_expense: 0,
        incentive_compensation_expense: 0,
        discretionary_expense: 0,
        deferred_cost_amortization: 0,
        deferred_origination_fee_amortization: 0,
        provision_expense: 0,
        non_interest_income: 0,
        non_interest_expense: 0,
        net_income: 0,
        // For balances, we'll take the last month's value
        loan_balance: 0,
        line_balance: 0,
        deposit_balance: 0,
        total_assets: 0,
        total_liabilities: 0,
        total_equity: 0
      });
    }
    
    const yearData = yearMap.get(year);
    yearData.months.push(month);
  });
  
  // Calculate annual totals
  const annualSummary = [];
  yearMap.forEach((yearData, year) => {
    const annual = { index: yearData.index };
    
    // Sum flow items (income/expense)
    const flowFields = [
      'interest_income_loans', 'interest_income_lines', 'origination_fees',
      'unused_commitment_fees', 'interest_expense', 'salary_expense',
      'benefits_expense', 'incentive_compensation_expense', 'discretionary_expense',
      'deferred_cost_amortization', 'deferred_origination_fee_amortization',
      'provision_expense', 'non_interest_income', 'non_interest_expense', 'net_income'
    ];
    
    flowFields.forEach(field => {
      annual[field] = yearData.months.reduce((sum, month) => sum + (month[field] || 0), 0);
    });
    
    // For balance sheet items, take the last month's value
    const lastMonth = yearData.months[yearData.months.length - 1];
    const balanceFields = [
      'loan_balance', 'line_balance', 'deposit_balance',
      'total_assets', 'total_liabilities', 'total_equity'
    ];
    
    balanceFields.forEach(field => {
      annual[field] = lastMonth[field] || 0;
    });
    
    // Calculate efficiency ratio for the year
    const totalRevenue = annual.interest_income_loans + annual.interest_income_lines + 
                         annual.origination_fees + annual.unused_commitment_fees;
    if (totalRevenue > 0) {
      annual.efficiency_ratio = annual.non_interest_expense / totalRevenue;
    } else {
      annual.efficiency_ratio = null;
    }
    
    annualSummary.push(annual);
  });
  
  return annualSummary.sort((a, b) => new Date(a.index) - new Date(b.index));
};

/**
 * Calculate consolidated total metrics
 */
const calculateConsolidatedMetrics = (annualSummary, monthlySchedule) => {
  if (!annualSummary || annualSummary.length === 0) {
    return {};
  }
  
  // Calculate totals across all years
  const totalRevenue = annualSummary.reduce((sum, year) => 
    sum + year.interest_income_loans + year.interest_income_lines + 
    year.origination_fees + year.unused_commitment_fees, 0
  );
  
  const totalNetIncome = annualSummary.reduce((sum, year) => 
    sum + year.net_income, 0
  );
  
  // Calculate averages for balance sheet items
  const avgLoanBalance = annualSummary.reduce((sum, year) => 
    sum + year.loan_balance, 0) / annualSummary.length;
  
  const avgLineBalance = annualSummary.reduce((sum, year) => 
    sum + year.line_balance, 0) / annualSummary.length;
  
  const avgDepositBalance = annualSummary.reduce((sum, year) => 
    sum + year.deposit_balance, 0) / annualSummary.length;
  
  const avgTotalAssets = annualSummary.reduce((sum, year) => 
    sum + year.total_assets, 0) / annualSummary.length;
  
  const avgTotalEquity = annualSummary.reduce((sum, year) => 
    sum + year.total_equity, 0) / annualSummary.length;
  
  const earningAssets = avgLoanBalance + avgLineBalance;
  
  // Calculate ratios (with guards for division by zero)
  const metrics = {
    total_revenue: totalRevenue,
    total_net_income: totalNetIncome,
    average_loan_balance: avgLoanBalance,
    average_line_balance: avgLineBalance,
    average_deposit_balance: avgDepositBalance,
    average_total_assets: avgTotalAssets,
    average_total_equity: avgTotalEquity,
    average_earning_assets: earningAssets
  };
  
  // Calculate NIM
  if (earningAssets > 0) {
    const netInterestIncome = annualSummary.reduce((sum, year) => 
      sum + year.interest_income_loans + year.interest_income_lines + 
      year.origination_fees - year.interest_expense - year.deferred_cost_amortization, 0
    );
    metrics.net_interest_margin = netInterestIncome / earningAssets / annualSummary.length;
  } else {
    metrics.net_interest_margin = null;
  }
  
  // Calculate ROA
  if (avgTotalAssets > 0) {
    metrics.return_on_average_assets = totalNetIncome / avgTotalAssets / annualSummary.length;
  } else {
    metrics.return_on_average_assets = null;
  }
  
  // Calculate ROE
  if (avgTotalEquity > 0) {
    metrics.return_on_average_equity = totalNetIncome / avgTotalEquity / annualSummary.length;
  } else {
    metrics.return_on_average_equity = null;
  }
  
  return metrics;
};

/**
 * Calculate cumulative payback period in years
 */
const calculateCumulativePayback = (monthlySchedule) => {
  if (!monthlySchedule || monthlySchedule.length === 0) {
    return null;
  }
  
  // Find first month where cumulative profit >= 0
  const breakEvenMonth = monthlySchedule.find(month => month.cumulative_profit >= 0);
  
  if (!breakEvenMonth) {
    return null; // No payback achieved
  }
  
  // Calculate years from start
  const startDate = new Date(monthlySchedule[0].index);
  const breakEvenDate = new Date(breakEvenMonth.index);
  const monthsDiff = (breakEvenDate.getFullYear() - startDate.getFullYear()) * 12 + 
                     (breakEvenDate.getMonth() - startDate.getMonth()) + 1;
  
  return monthsDiff / 12; // Convert to years
};